/* Round numbers for axes.
 *
 * The float-error-proofing is not decoration. An axis stepping by 0.1 from 0 reaches
 * 0.30000000000000004 on the third tick in IEEE 754, and a label reading that is worse than no
 * label at all.
 */

/**
 * How many decimal places a number needs to be written exactly.
 *
 * Non-finite first, because the loop has no exit for `NaN`: `Math.round(NaN * e) / e !== NaN` holds
 * at every scale, and once `e` overflows to `Infinity` the quotient stays `NaN` - measured, 29
 * million iterations and still going. That matters more than a wrong number would, since the
 * callers here run inside a `requestAnimationFrame` callback, where the symptom is a frozen tab.
 *
 * Guarded here rather than at each entry point: `floatErrorProofAdd` and `floatErrorProofMultiply`
 * are public exports of this package and reach it directly.
 */
const countDecimals = (num: number): number => {
  if (!Number.isFinite(num)) return 0;
  let e = 1,
    result = 0;
  while (Math.round(num * e) / e !== num) {
    e *= 10;
    result++;
  }
  return result;
};

const floatErrorProofMagnitude = (power: number): number =>
  +(10 ** power).toFixed(Math.max(0, 0 - power));

export const floatErrorProofMultiply = (a: number, b: number): number =>
  +(a * b).toFixed(countDecimals(a) + countDecimals(b));

export const floatErrorProofAdd = (a: number, b: number): number =>
  +(a + b).toFixed(Math.max(countDecimals(a), countDecimals(b)));

/**
 * The smallest round number at or above `num`: 1, 2 or 5 times a power of ten.
 *
 * The set people read without doing arithmetic. 2.5 and 7.5 are round enough to write down and not
 * round enough to count in.
 *
 * Defined for positive input. Anything else answers `NaN` rather than throwing: `Math.log10(0)` is
 * `-Infinity`, which reaches `(0).toFixed(Infinity)` and a `RangeError`, while a negative already
 * answered `NaN` - so the two disagreed. `NaN` also cannot hang a caller stepping by the result,
 * where 0 would.
 */
export const nice = (num: number): number => {
  if (!(num > 0)) return NaN;
  const log = Math.floor(Math.log10(num));
  const magnitude = floatErrorProofMagnitude(log);
  return floatErrorProofMultiply(
    [1, 2, 5].find(basic => num / magnitude <= basic) || 10,
    magnitude
  );
};

const goodTickCount = (pixels: number, divider = 40): number =>
  pixels / divider;

/**
 * Absolute, so a descending pair answers the same as the ascending one.
 *
 * A signed span makes `Math.log10` of a negative, which is `NaN`, and `nice(NaN)` used to reach a
 * loop with no exit for it.
 */
const spanOf = ([min, max]: readonly [number, number]): number =>
  Math.abs(max - min);

/** An interval that divides `visibleRange` into about one tick per `pixelsDivider` pixels. */
export const getRoundedInterval = (
  visibleRange: readonly [number, number],
  pixels = 100,
  pixelsDivider = 40
): number =>
  nice(nice(spanOf(visibleRange)) / goodTickCount(pixels, pixelsDivider));

/** The next round step up from `step`, walking the 1-2-5 ladder. */
const nextStepUp = (step: number): number => {
  const log = Math.floor(Math.log10(step));
  const magnitude = floatErrorProofMagnitude(log);
  const mantissa = step / magnitude;
  if (mantissa < 1.5) return floatErrorProofMultiply(2, magnitude);
  if (mantissa < 3.5) return floatErrorProofMultiply(5, magnitude);
  return floatErrorProofMultiply(1, floatErrorProofMagnitude(log + 1));
};

/**
 * A range that contains `range` and is exactly `tickCount` round steps wide.
 *
 * Which is a stronger thing than rounding the ends, and the difference is the whole point. An axis
 * that snaps its min and max to multiples of a round step still labels
 * `min + k · (span / tickCount)`, and that quotient is only round if the step count happens to
 * divide - so the ends read nicely and every tick between them does not. Fixing the *count* is
 * what makes every label round, and it is also what keeps the labels on the gridlines: a caller
 * drawing `tickCount` evenly spaced rules gets one per step by construction rather than by luck.
 *
 * The step walks up the 1-2-5 ladder until `tickCount` of it covers the range. Widening the range
 * instead would need more steps, which is the thing being held fixed; and a first guess of
 * `nice(span / tickCount)` can genuinely need one or two rungs, because snapping the low end
 * outward can push the far end past the last step.
 *
 * A zero-width or descending range comes back untouched. A single distinct value has no span to be
 * round about, and there is no count of steps that makes one.
 */
export const niceRangeForTicks = (
  range: readonly [number, number],
  tickCount: number
): [number, number] => {
  const span = spanOf(range);
  /* Ascending explicitly rather than through the sign of the span, which `spanOf` no longer
     carries. A descending pair is not an axis this can be round about; the caller has ordered it
     or it has not. */
  /* `Number.isInteger` rather than `tickCount >= 1`: a count of `NaN` passes every comparison -
     `NaN < 1` is `false` - and `nice(span / NaN)` walks into `countDecimals(NaN)`. A fractional
     count is not a count either. */
  if (
    !Number.isFinite(span) ||
    range[1] <= range[0] ||
    !Number.isInteger(tickCount) ||
    tickCount < 1
  ) {
    return [range[0], range[1]];
  }

  let step = nice(span / tickCount);
  for (let attempt = 0; attempt < 8; attempt++) {
    const min = floatErrorProofMultiply(step, Math.floor(range[0] / step));
    const max = floatErrorProofAdd(
      min,
      floatErrorProofMultiply(step, tickCount)
    );
    if (max >= range[1]) return [min, max];
    step = nextStepUp(step);
  }

  return [range[0], range[1]];
};

/**
 * Candidate division counts for an axis, tightest first once measured.
 *
 * Three to six, because that is the range over which a reader still reads the labels as a scale
 * rather than counting them, and it is enough for the 1-2-5 ladder to land close: every case
 * measured comes in under 1.27x the data span where a fixed four reached 2.5x.
 */
const AXIS_DIVISION_CANDIDATES: readonly [number, ...number[]] = [4, 5, 3, 6];

export type NiceAxis = {
  range: [number, number];
  tickCount: number;
};

/**
 * The roundest axis for a range, and how many divisions it took.
 *
 * Holding the count fixed is what wastes the box. `niceRangeForTicks` is exact about the count and
 * pays for it in span: with four divisions the step for `[0, 100]` is `nice(25)`, which is 50, so
 * the axis runs to 200 and the data occupies the bottom half. The 1-2-5 ladder cannot do better
 * *at four*, and there is nothing wrong with the ladder - five divisions of 20 fit `[0, 100]`
 * exactly.
 *
 * So the count is chosen rather than assumed, by the one measure that matters: how much of the
 * axis the data actually occupies. Ties go to the earlier candidate, which is why four leads the
 * list - it is the count the chart drew before this existed, so nothing moves for a range that was
 * already tight at four.
 *
 * The caller has to take the count as well as the range. An axis divided five ways and ruled four
 * ways puts every label between two gridlines, which reads as a rendering fault rather than a
 * choice about numbers.
 */
export const niceAxis = (
  range: readonly [number, number],
  /* Non-empty by type, because `candidates[0]` is the answer when there is nothing to measure
     against - an empty list would return `tickCount: undefined` past a signature promising a
     number, and take `niceRangeForTicks(range, undefined)` with it. */
  candidates: readonly [number, ...number[]] = AXIS_DIVISION_CANDIDATES
): NiceAxis => {
  const span = spanOf(range);
  if (!Number.isFinite(span) || range[1] <= range[0]) {
    return { range: [range[0], range[1]], tickCount: candidates[0] };
  }

  return candidates.reduce<NiceAxis>(
    (best, tickCount) => {
      const candidate = niceRangeForTicks(range, tickCount);
      const waste = candidate[1] - candidate[0];
      const bestWaste = best.range[1] - best.range[0];
      return waste < bestWaste ? { range: candidate, tickCount } : best;
    },
    {
      range: niceRangeForTicks(range, candidates[0]),
      tickCount: candidates[0],
    }
  );
};
